import classNames from 'classnames';
import { isAfter } from 'date-fns/esm';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import availTimes from '../../utils/availTimes';
import { parseISOLocal } from '../../utils/date';
import formatTimeString from '../../utils/formatTimeString';
import itemLimit from '../../utils/itemLimit';
import {
  packagePrice,
  packagePricePreTax,
  packagePriceTax
} from '../../utils/packagePrice';
import Price from '../common/Price';
import TickBox from '../common/TickBox';

let global_scrolled = false;

const scrollToRef = (ref, buffer = 0) => {
  window.scrollTo(0, Math.max(0, ref.current.offsetTop - buffer));
};

const useMountEffect = fn => {
  useEffect(fn, []);
};

const getUrlParameter = name => {
  name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  var regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  var results = regex.exec(location.search);
  return results == null || results[1].trim() == ''
    ? null
    : decodeURIComponent(results[1].replace(/\+/g, ' ').trim());
};

function scrollToItemQuery(scrolled, setScrolled, pkg, myRef, buffer) {
  const itemQuery = getUrlParameter('item');
  const scrollToItem = !!itemQuery;

  useMountEffect(() => {
    if (scrollToItem && !scrolled && itemQuery.trim() == pkg.item.trim()) {
      if (scrolled || global_scrolled) {
        return;
      }
      scrollToRef(myRef, buffer);
      setScrolled(true);
      global_scrolled = true;
    }
  });
}

const PackageItem = ({
  disabled = false,
  value = {},
  onChange,
  pkg,
  sale,
  config,
  loadingItemExpandedInLineObj
}) => {
  const myRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  scrollToItemQuery(
    scrolled,
    setScrolled,
    pkg,
    myRef,
    config.deepScrollBufferTop
  );

  const _availTimes = availTimes(pkg, sale);

  let availLimit = value.limit;
  if (!availLimit || config.limitToItemMaxQty) {
    availLimit = itemLimit(pkg, sale, config);
  }

  const now = new Date();
  const todaysTimes = _availTimes
    ? Object.keys(_availTimes)
        // make sure it's available
        .filter(timeProp => {
          var timeObj = _availTimes[timeProp];
          var parsedTime = timeObj['prop-start_time'].replace(
            /(\d{2})\/(\d{2})\/(\d{4}) (.+)/,
            '$3-$1-$2T$4'
          );
          var date = parseISOLocal(parsedTime);

          var timeIsFuture = isAfter(date, now);
          return timeObj['prop-m4s'] > 0 && timeIsFuture;
        })
    : [];
  const noneLeftToday = todaysTimes == null || todaysTimes.length < 1;
  return (
    <div
      ref={myRef}
      className={`swformfield-tktpackage item-id-${pkg.item_id}`}
    >
      <div className="swformfield-tktpackage__summary">
        <div className="swformfield-tktpackage__summary-header">
          {pkg.descrip}
        </div>
        <div className="swformfield-tktpackage__summary-description">
          {pkg.help_info}
        </div>
      </div>
      {/* <div className="swformfield-tktpackage__image">
        <img src={pkg.imageUrl} alt={pkg.item} />
      </div> */}

      <div className="swformfield-tktpackage__quantity">
        {_availTimes && noneLeftToday ? (
          <div className="swformfield-tktpackage__summary swformfield-tktpackage__summary--quantity">
            <div className="swformfield-tktpackage__summary-description">
              <i className="fa fa-info-circle" aria-hidden="true" />{' '}
              {config.tooLate ||
                'No more time slots are available for this day. Try selecting another day.'}
            </div>
          </div>
        ) : availLimit === 0 ? (
          <div className="swformfield-tktpackage__summary swformfield-tktpackage__summary--quantity">
            <div className="swformfield-tktpackage__summary-description">
              <i className="fa fa-exclamation-circle" aria-hidden="true" />{' '}
              {config.soldOut ||
                'This item is not available for puchase on this day.'}
            </div>
          </div>
        ) : (
          <div className={`swformfield swformfield--inputgroup`}>
            {!_availTimes ? (
              <label className="swformfield__label">Quantity</label>
            ) : (
              <>
                <label className="swformfield__label">
                  {/* className="dib mr3" style=
                {{ lineHeight: 'calc(0.375em + 34px)' }}> */}
                  Time
                </label>
                <div
                  className={classNames({
                    ['swformfieldwrapper']: true,
                    ['swformfieldwrapper--timeandqty']:
                      itemLimit(pkg, sale, config) > 1
                  })}
                >
                  <select
                    disabled={noneLeftToday}
                    value={value.time || ''}
                    className="swformfield__control form-control"
                    onChange={e => {
                      const time = e.target.value;
                      const option = e.target.options[e.target.selectedIndex];
                      const price = parseFloat(
                        option.getAttribute('data-price')
                      );
                      const pretax = parseFloat(
                        option.getAttribute('data-pretax')
                      );
                      const tax = parseFloat(option.getAttribute('data-tax'));
                      const limit = parseFloat(
                        option.getAttribute('data-limit')
                      );

                      // when item.limit is 1, we hide the qty selector, so
                      // here we attempt to auto populate qty=1 when you select
                      // a time slot, and remove qty when you deselect a time
                      const qty =
                        time != null &&
                        time.trim() != '' &&
                        itemLimit(pkg, sale, config) == 1 &&
                        (value.qty == null || value.qty < 1)
                          ? 1
                          : (time == null || time.trim() == '') &&
                            itemLimit(pkg, sale, config) == 1
                          ? 0
                          : value.qty;

                      onChange({ time, price, pretax, tax, qty, limit });
                    }}
                  >
                    <option value="">Select</option>
                    {todaysTimes.map(timeProp => {
                      var time = timeProp.replace('prop-', '');
                      var timeObj = _availTimes[timeProp];
                      var limit = parseInt(timeObj['prop-m4s'], 10);
                      limit = limit > -1 ? limit : 0;

                      var price = packagePrice(pkg, sale, time);
                      var pretax = packagePricePreTax(pkg, sale, time);
                      var tax = packagePriceTax(pkg, sale, time);

                      return (
                        <option
                          key={time}
                          value={time}
                          data-price={price}
                          data-pretax={pretax}
                          data-tax={tax}
                          data-limit={limit}
                        >
                          {formatTimeString(sale.day, time)}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </>
            )}
            {_availTimes && itemLimit(pkg, sale, config) == 1 ? (
              ''
            ) : (
              <TickBox
                max={availLimit}
                value={value.qty}
                onChange={qty => {
                  var time = value.time || null;
                  var price = packagePrice(pkg, sale, time);
                  var pretax = packagePricePreTax(pkg, sale, time);
                  var tax = packagePriceTax(pkg, sale, time);
                  onChange({
                    time,
                    price,
                    pretax,
                    tax,
                    qty,
                    limit: availLimit
                  });
                }}
                disabled={disabled || availLimit == 0}
              />
            )}

            <div className="swformfieldtotal">
              {config.retrieveDynamicPrices &&
              value.time &&
              loadingItemExpandedInLineObj &&
              loadingItemExpandedInLineObj.item_id === pkg.item_id &&
              loadingItemExpandedInLineObj.loading ? (
                <span>loading...</span>
              ) : config.taxInclusive ? (
                <Price price={packagePrice(pkg, sale, value.time)} />
              ) : (
                <Price
                  price={
                    value.pretax || packagePricePreTax(pkg, sale, value.time)
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
      {availLimit > 0 &&
        (!_availTimes || !noneLeftToday) &&
        (pkg.limit == null || pkg.limit > availLimit) &&
        availLimit <= config.limitThreshold && (
          <div className="swformfield-tktpackage__threshold">
            <div className="swformfield-tktpackage__summary swformfield-tktpackage__summary--quantity">
              <div className="swformfield-tktpackage__summary-description">
                Only {availLimit} left
              </div>
            </div>
          </div>
        )}
    </div>
  );
};

PackageItem.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  pkg: PropTypes.object,
  sale: PropTypes.object.isRequired,
  config: PropTypes.object,
  loadingItemExpandedInLineObj: PropTypes.object
};

export default PackageItem;
